import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, adminApi, errText } from "@/lib/api";
import { CallsTodayTab } from "@/components/admin/CallsTodayTab";
import { LeadsTab } from "@/components/admin/LeadsTab";
import { CustomersTab } from "@/components/admin/CustomersTab";
import { ConversationsTab } from "@/components/admin/ConversationsTab";
import { DashboardTab } from "@/components/admin/DashboardTab";

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("autohq_admin_token");
    if (!token) return setAuthed(false);
    adminApi
      .get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem("autohq_admin_token");
        setAuthed(false);
      });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", creds);
      localStorage.setItem("autohq_admin_token", data.token);
      setAuthed(true);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("autohq_admin_token");
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-[#71717A]">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-[#E4E4E7] rounded-sm p-8 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <img src="/logo-on-light.png" alt="AutoHQ" className="h-9" />
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37] border-l border-[#E4E4E7] pl-3">CRM</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">
            Sign in to AutoHQ CRM
          </h1>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Email</Label>
            <Input data-testid="admin-email-input" type="email" value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="mt-2 bg-[#FAFAFA] border-[#E4E4E7]" placeholder="admin@autohq.in" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Password</Label>
            <Input data-testid="admin-password-input" type="password" value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="mt-2 bg-[#FAFAFA] border-[#E4E4E7]" placeholder="••••••••" />
          </div>
          <Button data-testid="admin-login-button" type="submit" disabled={busy}
            className="w-full bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-bold py-5">
            {busy ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] text-left">
      <header className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-on-light.png" alt="AutoHQ" className="h-8" />
          <span className="hidden sm:inline text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37] border-l border-[#E4E4E7] pl-3">CRM</span>
        </div>
        <Button data-testid="admin-logout-button" onClick={logout} variant="outline"
          className="border-[#E4E4E7] bg-white text-[#52525B] hover:bg-[#FAFAFA] hover:text-[#0A0A0A] text-xs h-8">
          Logout
        </Button>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs defaultValue="calls">
          <TabsList className="bg-white border border-[#E4E4E7] p-1 h-auto">
            <TabsTrigger data-testid="admin-tab-calls" value="calls" className="text-xs font-semibold data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white">📞 Today's Calls</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-leads" value="leads" className="text-xs font-semibold data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white">📋 Leads</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-customers" value="customers" className="text-xs font-semibold data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white">💎 Care+ Customers</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-conversations" value="conversations" className="text-xs font-semibold data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white">💬 Chats</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-dashboard" value="dashboard" className="text-xs font-semibold data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white">📊 Analytics</TabsTrigger>
          </TabsList>
          <TabsContent value="calls"><CallsTodayTab /></TabsContent>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="customers"><CustomersTab /></TabsContent>
          <TabsContent value="conversations"><ConversationsTab /></TabsContent>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
